/**
 * Admin — Compliance Centre.
 * 23 seeded compliance items grouped by governance / notices / procedures /
 * people / security / clinical. Each has status (not_started → approved),
 * owner, approval + next-review dates, supporting-document reference and notes.
 *
 * There is NO auto "completed" status. Every state change is written to the
 * server-side audit trail.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Circle, FileCheck2, FileEdit, Clock, AlertTriangle } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";

const STATUS = {
  not_started:   { label: "Not started",  icon: Circle,        cls: "text-zinc-500" },
  draft:         { label: "Draft",        icon: FileEdit,      cls: "text-blue-400" },
  under_review:  { label: "Under review", icon: Clock,         cls: "text-amber-400" },
  approved:      { label: "Approved",     icon: FileCheck2,    cls: "text-emerald-400" },
  review_due:    { label: "Review due",   icon: AlertTriangle, cls: "text-red-400" },
};

const GROUP_LABEL = {
  governance: "Governance",
  notices: "Notices",
  procedures: "Procedures",
  people: "People",
  security: "Security",
  clinical: "Clinical",
};

export default function AdminCompliance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const { data } = await api.get("/compliance/items"); setItems(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const openEdit = (item) => {
    setOpenItem(item);
    setForm({
      status: item.status || "not_started",
      owner: item.owner || "",
      approval_date: item.approval_date || "",
      next_review_date: item.next_review_date || "",
      supporting_document: item.supporting_document || "",
      notes: item.notes || "",
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      // Send empty strings as null so the server unsets rather than storing ""
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      await api.patch(`/compliance/items/${openItem.key}`, payload);
      toast.success("Saved.");
      setOpenItem(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const grouped = items.reduce((acc, i) => { (acc[i.group] ||= []).push(i); return acc; }, {});
  const counts = Object.fromEntries(Object.keys(STATUS).map((k) => [k, items.filter((i) => i.status === k).length]));

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="fl-label text-[#f47a20]">Compliance</div>
        <h1 className="fl-heading text-3xl md:text-4xl mt-2">Compliance Centre</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Track the status of the policies, procedures and assurances required for UK clinical use. Nothing is auto-completed — every state change is recorded in the audit trail. This does not certify compliance; independent verification is required before real patient data is stored.
        </p>

        {/* Status summary */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="compliance-summary">
          {Object.entries(STATUS).map(([k, meta]) => {
            const Icon = meta.icon;
            return (
              <div key={k} className="fl-card p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                  <Icon size={14} className={meta.cls} />
                  <span className={meta.cls}>{meta.label}</span>
                </div>
                <div className="fl-heading text-3xl mt-2">{counts[k] || 0}</div>
              </div>
            );
          })}
        </div>

        {/* Groups */}
        <div className="mt-8 space-y-8" data-testid="compliance-groups">
          {loading && <div className="fl-card p-6 text-sm text-zinc-500">Loading…</div>}
          {Object.entries(grouped).map(([group, arr]) => (
            <section key={group}>
              <div className="fl-label mb-3 text-[#f47a20]">{GROUP_LABEL[group] || group}</div>
              <div className="fl-card overflow-hidden" data-testid={`compliance-group-${group}`}>
                {arr.map((it) => {
                  const meta = STATUS[it.status] || STATUS.not_started;
                  const Icon = meta.icon;
                  return (
                    <button
                      key={it.key}
                      onClick={() => openEdit(it)}
                      className="w-full text-left border-b p-4 hover:bg-white/5 transition-colors flex items-center gap-4"
                      style={{ borderColor: "var(--fl-border)" }}
                      data-testid={`compliance-row-${it.key}`}
                    >
                      <Icon size={16} className={meta.cls} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-zinc-100">{it.label}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">
                          {it.owner ? `Owner: ${it.owner}` : "No owner set"}
                          {it.next_review_date ? ` · Next review ${it.next_review_date}` : ""}
                          {it.supporting_document ? " · Doc attached" : ""}
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest ${meta.cls}`}>{meta.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>

      {openItem && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-[100]" data-testid="compliance-edit-modal">
          <div className="fl-card p-6 max-w-lg w-full">
            <div className="fl-label text-[#f47a20]">{openItem.label}</div>
            <div className="text-[11px] text-zinc-500 mt-1">Group: {GROUP_LABEL[openItem.group] || openItem.group} · Key: <code>{openItem.key}</code></div>

            <div className="mt-4 fl-label">Status</div>
            <select className="fl-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="compliance-edit-status">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>

            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div>
                <div className="fl-label mb-1">Owner</div>
                <input className="fl-input" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="e.g. Sam Reid" data-testid="compliance-edit-owner"/>
              </div>
              <div>
                <div className="fl-label mb-1">Supporting document</div>
                <input className="fl-input" value={form.supporting_document} onChange={(e) => setForm({ ...form, supporting_document: e.target.value })} placeholder="URL or filename" data-testid="compliance-edit-doc"/>
              </div>
              <div>
                <div className="fl-label mb-1">Approval date</div>
                <input type="date" className="fl-input" value={form.approval_date || ""} onChange={(e) => setForm({ ...form, approval_date: e.target.value })} data-testid="compliance-edit-approval"/>
              </div>
              <div>
                <div className="fl-label mb-1">Next review date</div>
                <input type="date" className="fl-input" value={form.next_review_date || ""} onChange={(e) => setForm({ ...form, next_review_date: e.target.value })} data-testid="compliance-edit-review"/>
              </div>
            </div>

            <div className="mt-3">
              <div className="fl-label mb-1">Notes</div>
              <textarea className="fl-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="compliance-edit-notes"/>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button className="fl-btn-ghost text-sm" onClick={() => setOpenItem(null)} data-testid="compliance-edit-cancel">Cancel</button>
              <button className="fl-btn text-sm" onClick={save} disabled={saving} data-testid="compliance-edit-save">
                {saving ? <Loader2 size={12} className="animate-spin"/> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
